import { useCallback } from "react";
import { useFetcher, useRevalidator } from "react-router-dom";

import Modal, { ModalButtonsSection, ModalContent, ModalSections, useCloseModal } from "components/Modal";
import { useBillingNavigation } from "v2/pages/billing/hooks/useBillingNavigation";
import { AnnualResubscribeContent } from "components/pricingoptions/AnnualResubscribeContent";
import { AutoUpgradeContent } from "components/pricingoptions/AutoUpgradeContent";
import { ChangePayAsYouGoContent, PayAsYouGoContent } from "components/pricingoptions/PayAsYouGoContent";
import SubmitButton from "components/SubmitButton";

import { useAutoUpgradePrompt } from "routes/data-routes/billing-data/pricing-options/hooks/useAutoUpgradePrompt";
import { useAutoUpgradeToggleAction } from "routes/data-routes/billing-data/pricing-options/hooks/useAutoUpgradeToggleAction";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { useCallbackAfterSubmission } from "v2/hooks/useCallbackAfterSubmission";
import { useToasterFromActionData } from "hooks/useToasterFromActionData";

import { isFetcherReloadingAfterSubmission } from "utils/fetcherUtils";


// Temporary: isRedesign overrides close to navigate to /billing instead of /.
// Remove once V1 billing is fully retired.
function useClosePricingOptionsModal(isRedesign?: boolean) {
  const { pricingOptionsFetcher } = usePricingOptions();
  const { goBack } = useBillingNavigation();

  const blockCloseCallback = useCallback(() => {
    return pricingOptionsFetcher?.state === "submitting";
  }, [ pricingOptionsFetcher?.state ]);

  const defaultClose = useCloseModal({ blockNavigationCallback: blockCloseCallback });
  const closeFn = isRedesign ? goBack : defaultClose;

  const closeModal = useCallback(() => {
    if (pricingOptionsFetcher?.state === "submitting") return;
    closeFn();
  }, [ closeFn, pricingOptionsFetcher?.state ]);

  // Show toast on success
  useToasterFromActionData(pricingOptionsFetcher);

  // also auto-close after successful actions
  useCallbackAfterSubmission(
    closeModal,
    pricingOptionsFetcher
  );

  return closeModal;
}

export function ConfirmEnableAutoUpgradeModal({ isRedesign }: { isRedesign?: boolean } = {}) {
  const autoUpgradePrompt = useAutoUpgradePrompt();
  const closeModal = useClosePricingOptionsModal(isRedesign);

  return (
    <Modal headline={ autoUpgradePrompt } onClose={ closeModal }>
      <AutoUpgradeContent showTitle={ false } />
    </Modal>
  );
}

export function ConfirmDisableAutoUpgradeModal({ isRedesign }: { isRedesign?: boolean } = {}) {
  const { pricingOptionsFetcher } = usePricingOptions();
  const closeModal = useClosePricingOptionsModal(isRedesign);
  const autoUpgradeToggleAction = useAutoUpgradeToggleAction();

  return (
    <Modal headline="Disable Auto-Upgrade" onClose={ closeModal }>
      <ModalContent>
        <ModalSections>
          Your service may pause if you reach your credit limit before your billing date with auto-upgrade off.
        </ModalSections>
        <ModalButtonsSection>
          <SubmitButton
            text="Disable Auto-Upgrade"
            className="button button-primary"
            size="MD"
            onClick={ () => autoUpgradeToggleAction(false) }
            fetchersToCheck={ [ pricingOptionsFetcher ] }
            disabled={ isFetcherReloadingAfterSubmission(pricingOptionsFetcher) }
          />
        </ModalButtonsSection>
      </ModalContent>
    </Modal>
  );
}

export function ConfirmEnablePayAsYouGoModal() {
  const closeModal = useClosePricingOptionsModal();

  return (
    <Modal headline="Enable pay-as-you-go" onClose={ closeModal }>
      <PayAsYouGoContent showTitle={ false } />
    </Modal>
  );
}

export function ConfirmChangePayAsYouGoModal({ isRedesign }: { isRedesign?: boolean } = {}) {
  // TRACKING: trackBillingV2LimitSettingChanged('payg', true/false) — fire after successful change
  const closeModal = useClosePricingOptionsModal(isRedesign);


  return (
    <Modal headline="Pay-as-you-go" onClose={ closeModal }>
      <ChangePayAsYouGoContent />
    </Modal>
  );
}

export function ConfirmResubscribeModal({ isRedesign }: { isRedesign?: boolean } = {}) {
  const { goBack } = useBillingNavigation();
  const defaultClose = useCloseModal();
  const closeModal = isRedesign ? goBack : defaultClose;
  const fetcher = useFetcher();
  const revalidator = useRevalidator();

  const isError = fetcher.state === "idle"
    && fetcher.data !== undefined
    && (fetcher.data as any)?.response === "error";

  // On v2 success, revalidate route loaders so the subscription page picks up the
  // renewed subscription state immediately (plan fields, useUser) instead of waiting
  // for the synchronizer. v1 path is left untouched.
  const redesignAfterSubmission = useCallback(() => {
    revalidator.revalidate();
    closeModal();
  }, [revalidator, closeModal]);

  // Only close the modal after a genuinely successful submission — not on error responses.
  // The default useCallbackAfterSubmission inside AnnualResubscribeContent would auto-close
  // on error because the { response: "error" } shape lacks the .error property that
  // getErrorFromFetcherData checks for. By passing afterSubmissionCallback only when there
  // is no error, we prevent the modal from closing on failure.
  const successCallback = isRedesign ? redesignAfterSubmission : closeModal;
  const afterSubmissionCallback = isError ? undefined : successCallback;

  return (
    <Modal headline="Resubscribe to yearly plan" onClose={ closeModal }>
      <AnnualResubscribeContent showTitle={ false } afterSubmissionCallback={ afterSubmissionCallback } formFetcher={ fetcher } isRedesign={ isRedesign } />
    </Modal>
  );
}
